import React from 'react';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {Button} from '@mui/material';
import ActionMenuItem, {ActionMenuItemProps, canShowActionMenuItem} from './ActionMenuItem';
import ActionMenuItemConfirmDialog from './ActionMenuItemConfirmDialog';
import {useResourceContext} from 'components/record/ResourceContext';
import {useRecordContext} from 'components/record/useRecordContext';
import {useDialogContext} from 'components/dialog/DialogContext';
import {useMutate} from 'hooks/useFetchData';
import {toast} from 'react-toastify';

type TogglePrivateConfirmButtonProps = {
  onSuccess?: () => void;
  onToggle: (nextIsPrivate: boolean) => Promise<unknown>;
};

const TogglePrivateConfirmButton = ({onSuccess, onToggle}: TogglePrivateConfirmButtonProps) => {
  const {close} = useDialogContext();

  const handleConfirm = () => {
    onToggle(true)
      .then(() => {
        close();
        onSuccess?.();
      })
      .catch(() => {});
  };

  return (
    <Button variant="contained" onClick={handleConfirm}>
      Make Private
    </Button>
  );
};

const ActionMenuItemTogglePrivate = (props: ActionMenuItemProps) => {
  const {record: competitor} = useRecordContext();
  const {params} = useResourceContext();
  const {onSuccess, permission, ...rest} = props;

  const [mutate] = useMutate<{item: {id: number; isPrivate: boolean}}>('updateCompetitorPrivate', 'Competitor');

  const isPrivate = Boolean(competitor.isPrivate);

  const handleToggle = (nextIsPrivate: boolean) => {
    if (typeof mutate !== 'function') {
      return Promise.resolve();
    }

    return mutate({
      variables: {
        subEventId: params.subEventId,
        id: competitor.id,
        isPrivate: nextIsPrivate
      }
    })
      .then(() => {
        toast.success(nextIsPrivate ? 'Track is now private' : 'Track is now public');
      })
      .catch((error) => {
        if (error.message) {
          toast.error(error.message);
        }
        throw error;
      });
  };

  if (!competitor.userId || !canShowActionMenuItem(permission)) {
    return null;
  }

  const handleMakePublic: React.MouseEventHandler = (e) => {
    e.stopPropagation();
    handleToggle(false)
      .then(() => onSuccess?.())
      .catch(() => {});
  };

  if (isPrivate) {
    return <ActionMenuItem label="Make Public" icon={<VisibilityOff />} onClick={handleMakePublic} {...rest} />;
  }

  return (
    <ActionMenuItemConfirmDialog
      label="Make Private"
      icon={<Visibility />}
      confirmTitle="Make track private?"
      confirmContent="This track will no longer be visible to other users. You can make it public again at any time."
      primaryButton={<TogglePrivateConfirmButton onToggle={handleToggle} onSuccess={onSuccess} />}
      {...rest}
    />
  );
};

export default ActionMenuItemTogglePrivate;
